import {EdgeParams, getSmartEdge, GetSmartEdgeOptions} from '@tisoap/react-flow-smart-edge';
import {Edge, getBezierPath, Node, Position} from 'reactflow';

import {
  FlowchartBaseEdge,
  EdgeLayoutData,
  ComputedEdgeLayoutData,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  interactiveNodeWrapperBorderWidth,
  sourceHandleWrapperPadding,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {ProcessFlowchartNodeType} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {backEdgeExists} from 'processes/graph-builder-processes/flowchart-viz/util/backEdgeExists';
import transformPointsToSvgPath from 'processes/graph-builder-processes/flowchart-viz/util/transformPointsToSvgPath';
import * as logging from 'util/Logging';

const SMART_EDGE_PADDING = 2;
const SMART_EDGE_NODE_PADDING = 2;
const SMART_EDGE_GRID_RATIO = 10;
const EDGE_INTERACTION_WIDTH = 25;

export function getEdgeRenderingData(
  baseEdgeData: FlowchartBaseEdge,
  edgeParams: EdgeParams,
  nodes: Array<Node>,
  edges: Array<Edge>,
  isInteractive: boolean = false,
  flowchartClientId: string,
  sourceNode?: Node,
  targetNode?: Node,
  // auto layout calculated edge info. When a user drags a node (and breaks autolayout) we fall back to
  // drawing our own edges between nodes and this will be nulled out.
  layoutData?: EdgeLayoutData
): ComputedEdgeLayoutData {
  const {sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition} = edgeParams;
  let offsetTargetY = targetY - (targetNode?.data.instanceData.nameHeight ?? 0);
  const rootNode = nodes.find((node) => node.type === ProcessFlowchartNodeType.START_NODE);
  const isLoopEdge =
    rootNode != null
      ? backEdgeExists(
          baseEdgeData.fromNodeClientId,
          baseEdgeData.toNodeClientId,
          edges,
          rootNode.data.clientId
        )
      : false;
  let [offsetSourceX, offsetSourceY, offsetTargetX] = [sourceX, sourceY, targetX];
  let [offsetSourcePosition, offsetTargetPosition] = [sourcePosition, targetPosition];
  const sourceNodeInstanceData = sourceNode?.data?.instanceData!;
  const targetNodeInstanceData = targetNode?.data?.instanceData!;
  if (isLoopEdge) {
    const sourceNodeDimensions = {
      height: sourceNodeInstanceData.height,
      width: sourceNodeInstanceData.width,
      nameHeight: sourceNodeInstanceData.nameHeight,
    };
    const targetNodeDimensions = {
      height: targetNodeInstanceData.height,
      width: targetNodeInstanceData.width,
      nameHeight: targetNodeInstanceData.nameHeight,
    };
    ({
      offsetSourceX,
      offsetSourceY,
      offsetTargetX,
      offsetTargetY,
      offsetSourcePosition,
      offsetTargetPosition,
    } = getLoopOffsetPositions(
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourceNodeDimensions,
      targetNodeDimensions
    ));
  }
  const offsetEdgeParams = {
    sourceX: offsetSourceX,
    sourceY: offsetSourceY,
    sourcePosition: offsetSourcePosition,
    targetX: offsetTargetX,
    targetY: offsetTargetY,
    targetPosition: offsetTargetPosition,
  };

  const edgePathAndLabelCoordinates = getEdgePathAndLabelCoordinates(
    offsetEdgeParams,
    isInteractive,
    isLoopEdge,
    nodes,
    baseEdgeData.clientId,
    flowchartClientId,
    layoutData
  );

  const hoverAreaWidth = EDGE_INTERACTION_WIDTH;
  const hoverAreaHeight = EDGE_INTERACTION_WIDTH;

  const hoverAreaX = edgePathAndLabelCoordinates.labelX - hoverAreaWidth / 2;
  const hoverAreaY = edgePathAndLabelCoordinates.labelY - hoverAreaHeight / 2;

  return {
    ...edgePathAndLabelCoordinates,
    hoverAreaWidth,
    hoverAreaHeight,
    hoverAreaX,
    hoverAreaY,
  };
}

function getLoopOffsetPositions(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  sourceNodeDimensions: {height: number; width: number; nameHeight?: number},
  targetNodeDimensions: {height: number; width: number; nameHeight?: number}
): {
  offsetSourceX: number;
  offsetSourceY: number;
  offsetTargetX: number;
  offsetTargetY: number;
  offsetSourcePosition: Position;
  offsetTargetPosition: Position;
} {
  const sourceOffset = sourceNodeDimensions.width / 2 + SMART_EDGE_PADDING;
  const targetOffset = targetNodeDimensions.width / 2 + SMART_EDGE_PADDING;
  // If the target is to the left of the source, edge should be to the right. Otherwise, it should be to the left.
  const isTargetToLeftOfSource = targetX < sourceX;

  const {offsetSourceX, offsetTargetX, offsetSourcePosition, offsetTargetPosition} = isTargetToLeftOfSource
    ? {
        offsetSourceX: sourceX + sourceOffset,
        offsetTargetX: targetX + targetOffset,
        offsetSourcePosition: Position.Right,
        offsetTargetPosition: Position.Right,
      }
    : {
        offsetSourceX: sourceX - sourceOffset,
        offsetTargetX: targetX - targetOffset,
        offsetSourcePosition: Position.Left,
        offsetTargetPosition: Position.Left,
      };
  const offsetSourceY = sourceY - sourceNodeDimensions.height / 2 - (sourceNodeDimensions.nameHeight ?? 0);
  const offsetTargetY = targetY + targetNodeDimensions.height / 2;

  return {
    offsetSourceX,
    offsetSourceY,
    offsetTargetX,
    offsetTargetY,
    offsetSourcePosition,
    offsetTargetPosition,
  };
}

export function getEdgePathAndLabelCoordinates(
  edgeParams: EdgeParams,
  isInteractive: boolean,
  isLoopEdge: boolean,
  nodes: Array<Node>,
  edgeId: string,
  flowchartId: string,
  layoutData?: EdgeLayoutData
): {
  edgePath: string;
  labelX: number;
  labelY: number;
} {
  if (layoutData?.points != null && layoutData?.labelX != null && layoutData?.labelY != null) {
    let labelX = layoutData.labelX;
    let labelY = layoutData.labelY;

    const pointCorrectionX = isInteractive
      ? interactiveNodeWrapperBorderWidth + sourceHandleWrapperPadding
      : 0;
    const pointCorrectionY = isInteractive
      ? sourceHandleWrapperPadding + interactiveNodeWrapperBorderWidth
      : 0;

    labelX += pointCorrectionX;
    labelY += pointCorrectionY;

    const path = transformPointsToSvgPath(layoutData.points, pointCorrectionX, pointCorrectionY);
    return {edgePath: path, labelX, labelY};
  } else {
    if (isLoopEdge) {
      const smartEdgeOptions: GetSmartEdgeOptions = {
        nodePadding: SMART_EDGE_NODE_PADDING,
        gridRatio: SMART_EDGE_GRID_RATIO,
      };

      const smartEdge = getSmartEdge({
        ...edgeParams,
        nodes,
        options: smartEdgeOptions,
      });
      // It is possible for smartEdge to be null if the SmartEdge library fails to calculate a path.
      // In this case, we fall back on the default edge renderer, which may overlap with other edges.
      if (smartEdge) {
        return {
          edgePath: smartEdge.svgPathString,
          labelX: smartEdge.edgeCenterX,
          labelY: smartEdge.edgeCenterY,
        };
      } else {
        logging.info('SmartEdge failed to calculate path', {
          extra: {
            edgeId,
            flowchartId,
            isEdgeInteractive: isInteractive,
            numNodes: nodes.length,
          },
        });
      }
    }

    const [path, labelX, labelY] = getBezierPath(edgeParams);
    return {edgePath: path, labelX, labelY};
  }
}
